/**
 * API Configuration - Centralized API settings and utilities
 */

// API base URL - defaults to localhost:3000 for development
// Can be overridden with environment variable
// Note: Environment variables must be available at BUILD TIME, not runtime
// For production builds, set VUE_APP_API_URL before running npm run build
// Or create a .env.production file with: VUE_APP_API_URL=https://dietedics.com
var API_BASE_URL = process.env.VUE_APP_API_URL || 'https://dietedics.com'

if (process.env.NODE_ENV !== 'production') {
  API_BASE_URL = 'http://localhost:3000'
}

// Debug: Log the API base URL (will be embedded at build time)
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_BASE_URL)
}

/**
 * Get the full API URL for an endpoint
 * @param {string} endpoint - API endpoint (e.g., '/users/sign_in')
 * @returns {string} Full API URL
 */
export function getApiUrl(endpoint) {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  // Ensure endpoint starts with /api/ prefix
  // If endpoint already starts with /api/, use it as-is, otherwise prepend /api
  const apiEndpoint = cleanEndpoint.startsWith('/api/') ? cleanEndpoint : `/api${cleanEndpoint}`
  return `${API_BASE_URL}${apiEndpoint}`
}

/**
 * Get default headers for API requests
 * @param {boolean} includeAuth - Whether to include authentication
 * @returns {Object} Headers object
 */
export function getDefaultHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }

  // For Devise with API mode, we use session-based authentication
  // The session is stored in httpOnly cookies, which are automatically
  // sent with requests when credentials: 'include' is set
  // We don't need to manually add Authorization headers for session-based auth
  
  // If you switch to token-based auth later, uncomment this:
  // if (includeAuth) {
  //   const token = getAuthToken()
  //   if (token) {
  //     headers['Authorization'] = `Bearer ${token}`
  //   }
  // }

  return headers
}

/**
 * Get authentication token from secure storage
 * @returns {string|null} Auth token or null
 */
// function getAuthToken() {
//   try {
//     const authData = localStorage.getItem('dietedics_auth')
//     if (!authData) return null
    
//     const auth = JSON.parse(authData)
//     // Check if token is expired
//     if (auth.expiresAt && new Date(auth.expiresAt) < new Date()) {
//       return null
//     }
    
//     return auth.token || null
//   } catch (error) {
//     console.error('Error getting auth token:', error)
//     return null
//   }
// }

/**
 * Make an API request with error handling
 * @param {string} endpoint - API endpoint (e.g., '/courses' or '/users/sign_in')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function apiRequest(endpoint, options = {}) {
  // getApiUrl handles adding /api prefix, so just pass the endpoint as-is
  const url = getApiUrl(endpoint)
  const headers = getDefaultHeaders()
  
  // Merge custom headers
  if (options.headers) {
    Object.assign(headers, options.headers)
  }

  const config = {
    ...options,
    headers,
    credentials: 'include' // Include cookies for CORS - always include for session-based auth
  }

  try {
    const response = await fetch(url, config)
    
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type')
    const isJson = contentType && contentType.includes('application/json')
    
    let data = null
    if (isJson) {
      data = await response.json()
    } else {
      data = await response.text()
    }

    // Handle error responses
    if (!response.ok) {
      const error = new Error(data.message || data.error || `HTTP ${response.status}: ${response.statusText}`)
      error.status = response.status
      error.data = data
      throw error
    }

    return data
  } catch (error) {
    // Re-throw API errors
    if (error.status) {
      throw error
    }
    
    // Handle network errors
    throw new Error(`Network error: ${error.message}`)
  }
}

export { API_BASE_URL }

